// ============================================
// @gs/ui — Toast notifications
// ============================================

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: number;
  message: string;
  variant: ToastType;
}

interface ToastContextValue {
  toast: (message: string, variant?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const VARIANT_STYLES: Record<ToastType, { border: string; iconColor: string; icon: typeof CheckCircle }> = {
  success: { border: 'border-l-green-500',  iconColor: 'text-green-500',  icon: CheckCircle },
  error:   { border: 'border-l-red-500',    iconColor: 'text-red-500',    icon: XCircle },
  warning: { border: 'border-l-amber-500',  iconColor: 'text-amber-500',  icon: AlertTriangle },
  info:    { border: 'border-l-blue-500',   iconColor: 'text-blue-500',   icon: Info },
};

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((message: string, variant: ToastType = 'info') => {
    const id = ++nextId;
    setToasts(prev => [...prev, { id, message, variant }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed right-4 top-4 z-[60] flex flex-col gap-2 max-w-[340px] w-full sm:w-auto">
        {toasts.map(t => {
          const { border, iconColor, icon: Icon } = VARIANT_STYLES[t.variant];
          return (
            <div key={t.id}
              className={`flex items-center gap-3 rounded-xl border-l-4 ${border}
                          bg-white dark:bg-slate-900
                          border border-slate-100 dark:border-slate-700
                          px-4 py-3 shadow-md animate-slide-in`}>
              <Icon size={18} className={`shrink-0 ${iconColor}`} />
              <span className="flex-1 text-sm font-medium text-slate-800 dark:text-slate-100">
                {t.message}
              </span>
              <button type="button" onClick={() => dismiss(t.id)}
                className="shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors rounded-lg p-0.5"
                aria-label="Fermer">
                <X size={15} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
